import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginationParams } from '@console/utils';
import { groupsApi, Group, CreateGroupDto, UpdateGroupDto } from '@/lib/api/groups';
import { toast } from 'sonner';

export function useGroups(defaultParams?: PaginationParams) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: defaultParams?.page || 1,
    limit: defaultParams?.limit || 10,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchGroups = useCallback(
    async (params?: PaginationParams) => {
      try {
        setIsLoading(true);
        const response = await groupsApi.getAll(params);
        setGroups(response.data);
        setPagination({
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('groups.loadError'));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const createGroup = useCallback(
    async (groupData: CreateGroupDto) => {
      try {
        setIsSaving(true);
        const newGroup = await groupsApi.create(groupData);
        setGroups((prev) => [newGroup, ...prev]);
        setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
        toast.success(t('groups.createSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('groups.createError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const updateGroup = useCallback(
    async (id: string, groupData: UpdateGroupDto) => {
      try {
        setIsSaving(true);
        const updatedGroup = await groupsApi.update(id, groupData);
        setGroups((prev) => prev.map((group) => (group.id === id ? updatedGroup : group)));
        toast.success(t('groups.updateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('groups.updateError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const deleteGroup = useCallback(
    async (id: string) => {
      try {
        setIsSaving(true);
        await groupsApi.delete(id);
        setGroups((prev) => prev.filter((group) => group.id !== id));
        setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
        toast.success(t('groups.deleteSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('groups.deleteError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchGroups({
      page: defaultParams?.page || 1,
      limit: defaultParams?.limit || 10,
    });
  }, [fetchGroups, defaultParams?.page, defaultParams?.limit]);

  return {
    groups,
    pagination,
    isLoading,
    isSaving,
    createGroup,
    updateGroup,
    deleteGroup,
    refetch: fetchGroups,
  };
}
